import { useTranslation } from 'react-i18next';
import { Info, UserCog, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SimpleSelect } from '@/shared/ui/filters';
import { FormField } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui';
import { cn } from '@/lib/utils';

interface ContactSelectProps {
  label: string;
  contactId?: string;
  options: SelectOption[];
  isLoading: boolean;
  disabled?: boolean;
  showDetails?: boolean;
  required?: boolean;
  error?: string;
  onChange: (id: string | undefined) => void;
  onDetailsClick: () => void;
  onEditClick: () => void;
  onAddClick: () => void;
}

export const ContactSelect = ({
  label,
  contactId,
  options,
  isLoading,
  disabled = false,
  showDetails = false,
  required = false,
  error,
  onChange,
  onDetailsClick,
  onEditClick,
  onAddClick
}: ContactSelectProps) => {
  const { t } = useTranslation();

  return (
    <FormField label={`${label} ${t('common.contact', 'Contact')}`} name="contact" required={required} error={error}>
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <SimpleSelect
            options={options}
            value={contactId}
            onChange={onChange}
            isLoading={isLoading}
            placeholder={t('common.selectContact', 'Select Contact')}
            className="w-full"
            disabled={disabled}
          />
        </div>
        <div className="flex gap-1 shrink-0">
          {contactId && (
            <>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onDetailsClick}
                className={cn(
                  'w-11 border-slate-300 transition-colors',
                  showDetails
                    ? 'bg-green-100 border-green-400 text-green-700'
                    : 'bg-white hover:bg-slate-50 text-slate-600'
                )}
                title={t('contacts.showDetails', 'Show Details')}>
                <Info className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onEditClick}
                className="w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
                title={
                  disabled ? t('contacts.viewContact', 'View Contact') : t('contacts.editContact', 'Edit Contact')
                }>
                <UserCog className="w-4 h-4 text-slate-600" />
              </Button>
            </>
          )}
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onAddClick}
              className="w-11 border-slate-300 bg-white hover:bg-slate-50 transition-colors"
              title={t('contacts.addContact', 'Add Contact')}>
              <Plus className="w-4 h-4 text-slate-600" />
            </Button>
          )}
        </div>
      </div>
    </FormField>
  );
};
